import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Fab, useTheme, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// 导入组件
import TaskList from '../components/Tasks/TaskList';
import TaskFilter from '../components/Tasks/TaskFilter';
import TaskPagination from '../components/Tasks/TaskPagination';
import UploadDialog from '../components/Tasks/UploadDialog';
import EnhancedEmptyState from '../components/Common/EnhancedEmptyState';
import LoginModal from '../components/Common/LoginModal';

// 导入操作
import { fetchTasks, uploadFiles, startTaskPolling, stopTaskPolling } from '../store/actions/tasksActions';
import { fetchLanguages, fetchStatuses, showNotification } from '../store/actions/uiActions';

const Trans = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openUpload, setOpenUpload] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginTriggerSource, setLoginTriggerSource] = useState(null); // 'empty-area' | 'fab-button'
  const { tasks, loading, total, page, limit } = useSelector((state) => state.tasks);
  const { isAuthenticated, authChecked } = useSelector((state) => state.auth);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    language: 'all',
    status: 'all',
  });
  
  // 判断是否已筛选
  const [hasFiltered, setHasFiltered] = useState(false);
  // 页面初始化状态
  const [pageInitialized, setPageInitialized] = useState(false);
  
  // 初始加载任务列表和开始任务轮询
  useEffect(() => {
    // 只有在认证状态检查完成后才执行
    if (authChecked) {
      if (isAuthenticated) {
        // 加载任务数据
        updateTaskList();
        
        // 加载语种和状态数据
        dispatch(fetchLanguages());
        dispatch(fetchStatuses());
        
        // 开始轮询进行中的任务
        dispatch(startTaskPolling([], 30000)); // 每30秒轮询一次
        
        // 组件卸载时停止轮询
        return () => {
          dispatch(stopTaskPolling());
        };
      }
      // 标记页面已初始化
      setPageInitialized(true);
    }
  }, [authChecked, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 当任务列表发生变化时，重新评估需要轮询的任务
  useEffect(() => {
    if (isAuthenticated && tasks && tasks.length > 0) {
      // 找出所有进行中的任务
      const inProgressTaskIds = tasks
        .filter(task => task.status === 'processing' || task.status === 'waiting')
        .map(task => task.task_id);
      
      // 如果有进行中的任务，更新轮询
      if (inProgressTaskIds.length > 0) {
        dispatch(startTaskPolling(inProgressTaskIds, 30000)); // 进行中任务每30秒轮询
      }
    }
  }, [tasks, dispatch, isAuthenticated]);

  // 获取任务列表数据
  const updateTaskList = (page = 1, newFilters = filters) => {
    // 检查是否应用了筛选条件
    const isFiltered = 
      newFilters.startDate !== '' || 
      newFilters.endDate !== '' || 
      newFilters.language !== 'all' || 
      newFilters.status !== 'all';
    
    setHasFiltered(isFiltered);
    
    dispatch(fetchTasks({
      page,
      limit,
      language: newFilters.language !== 'all' ? newFilters.language : undefined,
      status: newFilters.status !== 'all' ? newFilters.status : undefined,
      startDate: newFilters.startDate || undefined,
      endDate: newFilters.endDate || undefined
    }));
  };
  
  // 处理筛选变化
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (isAuthenticated) {
      updateTaskList(1, newFilters);
    }
  };

  // 处理页码变化
  const handlePageChange = (newPage) => {
    if (isAuthenticated) {
      updateTaskList(newPage, filters);
    }
  };

  // 处理打开上传对话框
  const handleOpenUpload = (source = 'fab-button') => {
    // 如果用户未登录，显示登录弹窗
    if (!isAuthenticated) {
      setLoginTriggerSource(source); // 记录触发源
      setShowLoginModal(true);
      return;
    }
    
    // 已登录用户直接打开上传对话框
    setOpenUpload(true);
  };

  // 关闭上传对话框
  const handleCloseUpload = () => {
    setOpenUpload(false);
  };

  // 处理文件上传完成
  const handleUploadComplete = async (files, language, certification_type = 'none', is_urgent = false) => {
    console.log('🔍 Trans.js handleUploadComplete 接收到的数据:', {
      '文件数量': files?.length,
      '翻译语种': language,
      '认证类型': certification_type,
      '是否加急': is_urgent
    });
    
    // 再次检查登录状态
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return Promise.reject(new Error('用户未登录'));
    }

    try {
      // 调用上传文件API，传递认证类型和加急标志
      const response = await dispatch(uploadFiles(files, language, certification_type, is_urgent));
      console.log('✅ [Trans] 上传成功:', {
        '上传状态': response?.success,
        '积分余额': response?.data.points_balance,
        '所需积分': response?.data.points_required,
      });
      
      // 上传成功后刷新任务列表
      updateTaskList(1, filters);
      
      // 关闭上传对话框
      setOpenUpload(false);
      
      return response;
    } catch (error) {
      // 上传失败，保持对话框开启，不刷新任务列表
      console.log('📋 [Trans] 上传请求处理完成:', {
        isHandledError: error.handled,
        errorType: error.originalError?.response?.status || 'unknown'
      });
      
      // 错误通知已经在uploadFiles action中处理
      // 403错误（点数不足）等已处理错误不需要额外操作
      // 页面保持当前状态，让用户可以继续操作
      
      // 对于已处理的错误（如403点数不足），返回resolved Promise避免中断流程
      if (error.handled) {
        console.log('✅ [Trans] 已处理错误，继续流程');
        return Promise.resolve({ handled: true, error });
      }
      
      // 对于未处理的错误，根据错误类型进行处理
      console.error('❌ [Trans] 未处理的上传错误:', error);
      
      // 处理超时错误
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        dispatch(showNotification('请求超时，请检查网络连接后重试', 'error'));
        return Promise.resolve({ handled: true, error });
      }
      
      // 处理网络错误
      if (error.message?.includes('Network Error') || !error.response) {
        dispatch(showNotification('网络连接失败，请检查网络后重试', 'error'));
        return Promise.resolve({ handled: true, error });
      }
      
      // 其他未知错误
      dispatch(showNotification('上传失败，请稍后重试', 'error'));
      return Promise.resolve({ handled: true, error });
    }
  };

  // 处理空白上传区域的上传
  const handleEmptyAreaUpload = (files, language, certification_type = 'none', is_urgent = false) => {
    // 如果没有文件和语言参数，说明是未登录用户点击拖放区域触发的登录检查
    if (!files || !language) {
      setLoginTriggerSource('empty-area');
      setShowLoginModal(true);
      return;
    }
    
    // 有文件和语言参数，说明是已登录用户的实际文件上传
    handleUploadComplete(files, language, certification_type, is_urgent);
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    //console.log('登录成功，用户数据:', userData);
    setShowLoginModal(false);
    // 登录成功后加载任务列表
    updateTaskList();
    
    // 只有从浮动按钮触发的登录才在成功后打开上传对话框
    // 从空白上传区域触发的登录成功后应该保持在空白上传区域
    if (loginTriggerSource === 'fab-button') {
      setOpenUpload(true);
    }
    
    // 重置触发源
    setLoginTriggerSource(null);
  };

  // 清除筛选条件
  const clearFilters = () => {
    const resetFilters = {
      startDate: '',
      endDate: '',
      language: 'all',
      status: 'all',
    };
    setFilters(resetFilters);
    if (isAuthenticated) {
      updateTaskList(1, resetFilters);
    }
  };

  // 判断是否有任务数据
  const hasTaskData = tasks && tasks.length > 0;
  
  // 判断是否是初始状态（没有筛选且没有数据）
  const isInitialEmpty = !hasTaskData && !hasFiltered;
  
  // 判断是否是筛选结果为空
  const isFilteredEmpty = !hasTaskData && hasFiltered;

  // 状态1: 初始loading状态 - 只在认证状态未检查完成时显示
  if (!authChecked) {
    return (
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            正在加载...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ 
        height: '100%',
        overflowY: 'scroll',
        scrollbarWidth: 'none', /* Firefox */
        '&::-webkit-scrollbar': {
          width: '0px',
          background: 'transparent', /* Chrome/Safari/Webkit */
          display: 'none'
        },
        '&': {
          msOverflowStyle: 'none' /* IE 10+ */
        }
      }}>
        {/* 状态2: 空白上传文件区域 - 未登录或已登录但无任务 */}
        {(!isAuthenticated || (isAuthenticated && isInitialEmpty)) ? (
          <EnhancedEmptyState onUpload={handleEmptyAreaUpload} />
        ) : (
          /* 状态3: 任务列表区域 - 已登录且有任务 */
          <>
            {/* 标题和信息 */}
            <Grid container spacing={3} sx={{ mb: 0 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 600,
                      background: 'linear-gradient(90deg, #4353FF 0%, #4353FF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      marginLeft: '10px'
                    }}
                  >
                    签证翻译记录
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      px: 2,
                      py: 0.75,
                      borderRadius: 8,
                      bgcolor: theme.palette.background.light,
                      display: 'flex',
                      alignItems: 'center',
                      mr: 2
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      共 {total} 个任务
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* 筛选器 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TaskFilter 
                  filters={filters} 
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                />
              </Grid>
            </Grid>

            {/* 任务列表或空状态 */}
            {isFilteredEmpty ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  border: '2px dashed',
                  borderColor: theme.palette.divider,
                  borderRadius: 3
                }}
              >
                <SearchOffIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  没有找到符合条件的翻译记录
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  请尝试调整筛选条件或清除所有筛选
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TaskList tasks={tasks} loading={loading} />
                  </Grid>
                </Grid>

                {/* 分页 */}
                {total > limit && (
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <TaskPagination 
                        page={page}
                        total={total}
                        limit={limit}
                        onPageChange={handlePageChange}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {/* 浮动上传按钮 - 只在状态3（任务列表状态）时显示 */}
        {isAuthenticated && !isInitialEmpty && (
          <Fab
            color="primary"
            aria-label="upload"
            onClick={handleOpenUpload}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1000,
              boxShadow: '0 8px 25px rgba(67, 83, 255, 0.3)',
              '&:hover': {
                boxShadow: '0 12px 35px rgba(67, 83, 255, 0.4)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* 上传对话框 */}
        <UploadDialog
          open={openUpload}
          onClose={handleCloseUpload}
          onComplete={handleUploadComplete}
          requireAuth={!isAuthenticated}
        />
      </Box>

      {/* 登录弹窗 */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default Trans;